import { Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './styles/global.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ColorModeProvider } from './contexts/ColorModeContext';
import React, { useEffect } from 'react';
import { ViewportProvider } from './components/layout/ViewportHandler';
import { Toaster } from './components/ui/toaster';

// Import pages
import Home from './pages/Home/index';
import MedicalHistory from './pages/MedicalHistory';
import Appointments from './pages/Appointments';
import TestResults from './pages/TestResults';
import Medications from './pages/Medications';
import MedicalRecords from './pages/MedicalRecords';
import HealthTools from './pages/HealthTools';
import Messages from './pages/Messages';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Debug from './pages/Debug';

// Import Phase 2 pages
import TestCatalog from './pages/TestCatalog';
import OrderTest from './pages/OrderTest';
import ScheduleAppointment from './pages/ScheduleAppointment';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Help from './pages/Help';
import AIAssistant from './pages/AIAssistant';
import HealthHub from './pages/HealthHub';
import CarePathway from './pages/CarePathway';

// Import Phase 3B pages
import ConsentManagement from './pages/ConsentManagement';

// Import Vault pages
import Vault from './pages/Vault';
import VaultSetup from './pages/VaultSetup';

// Import AppShell
import AppShell from './components/layout/app-shell';

// Import auth components
import { AuthRequired } from './contexts/auth-context';

// Import error handling components
import ErrorBoundary from './components/ui/ErrorBoundary';
import { ErrorProvider } from './contexts/error-context';

const DefaultErrorFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) => (
  <div className="p-8 bg-gray-50 dark:bg-gray-950 min-h-screen flex flex-col items-center justify-center">
    <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg max-w-lg w-full border border-gray-200 dark:border-gray-800">
      <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Something went wrong</h2>
      <p className="mb-4 text-gray-500">The application encountered an unexpected error.</p>
      <div className="mb-4 bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-auto max-h-32 text-red-600">
        {error.message || 'Unknown error'}
      </div>
      <button
        onClick={resetErrorBoundary}
        className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
      >
        Retry
      </button>
    </div>
  </div>
);

/** Wraps a page in AuthRequired + AppShell */
const ProtectedPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AuthRequired>
    <AppShell>{children}</AppShell>
  </AuthRequired>
);

const App: React.FC = () => {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Global error caught:', event.error);
    };
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  return (
    <ErrorBoundary FallbackComponent={DefaultErrorFallback}>
      <ErrorProvider>
        <ViewportProvider>
          <ColorModeProvider>
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
            <Toaster />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/debug" element={<Debug />} />

              {/* ==========================================
                  Home
                  ========================================== */}
              <Route path="/" element={<ProtectedPage><Home /></ProtectedPage>} />

              {/* ==========================================
                  Health section
                  ========================================== */}
              <Route path="/health" element={<ProtectedPage><Home /></ProtectedPage>} />
              <Route path="/medical-history" element={<ProtectedPage><MedicalHistory /></ProtectedPage>} />
              <Route path="/test-results" element={<ProtectedPage><TestResults /></ProtectedPage>} />
              <Route path="/medications" element={<ProtectedPage><Medications /></ProtectedPage>} />
              <Route path="/medical-records" element={<ProtectedPage><MedicalRecords /></ProtectedPage>} />
              <Route path="/health-tools" element={<ProtectedPage><HealthTools /></ProtectedPage>} />
              <Route path="/test-catalog" element={<ProtectedPage><TestCatalog /></ProtectedPage>} />
              <Route path="/order-test" element={<ProtectedPage><OrderTest /></ProtectedPage>} />

              {/* ==========================================
                  Care section
                  ========================================== */}
              <Route path="/care" element={<Navigate to="/appointments" replace />} />
              <Route path="/appointments/*" element={<ProtectedPage><Appointments /></ProtectedPage>} />
              <Route path="/schedule-appointment" element={<ProtectedPage><ScheduleAppointment /></ProtectedPage>} />
              <Route path="/care-pathways" element={<ProtectedPage><CarePathway /></ProtectedPage>} />
              <Route path="/messages" element={<ProtectedPage><Messages /></ProtectedPage>} />

              {/* ==========================================
                  Me section
                  ========================================== */}
              <Route path="/me" element={<Navigate to="/profile" replace />} />
              <Route path="/profile" element={<ProtectedPage><Profile /></ProtectedPage>} />
              <Route path="/settings" element={<ProtectedPage><Settings /></ProtectedPage>} />
              <Route path="/help" element={<ProtectedPage><Help /></ProtectedPage>} />

              {/* ==========================================
                  AI Health Companion
                  ========================================== */}
              <Route path="/ai-health-companion" element={<ProtectedPage><AIAssistant /></ProtectedPage>} />
              <Route path="/ai-assistant" element={<Navigate to="/ai-health-companion" replace />} />

              {/* ==========================================
                  Health Hub (placeholder until Phase 4)
                  ========================================== */}
              <Route path="/health-hub" element={<ProtectedPage><HealthHub /></ProtectedPage>} />
              <Route path="/health-hub/*" element={<ProtectedPage><HealthHub /></ProtectedPage>} />

              {/* ==========================================
                  Phase 3B: Consent Management
                  ========================================== */}
              <Route path="/consent" element={<ProtectedPage><ConsentManagement /></ProtectedPage>} />

              {/* ==========================================
                  Health Vault (encrypted on-device storage)
                  ========================================== */}
              <Route path="/vault" element={<ProtectedPage><Vault /></ProtectedPage>} />
              <Route path="/vault/setup" element={<ProtectedPage><VaultSetup /></ProtectedPage>} />

              {/* Catch-all */}
              <Route path="*" element={<ProtectedPage><Home /></ProtectedPage>} />
            </Routes>
          </ColorModeProvider>
        </ViewportProvider>
      </ErrorProvider>
    </ErrorBoundary>
  );
};

export default App;
